import { Button, FoodsIcon, FoodsTextInput } from "@foodpilot/foods";
import { Box, Popover, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { ViewFilters } from "../../hooks/viewFilters/useViewFilters";

type CreateViewButtonProps = {
    viewFilter: ViewFilters;
};
export const CreateViewButton = (props: CreateViewButtonProps) => {
    const { viewFilter } = props;
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const isOpen = Boolean(anchorEl);
    const handleClose = () => setAnchorEl(null);

    return (
        <Fragment key={viewFilter.selectedFilter?.filterName}>
            <Button
                variant="ternary"
                endIcon={<FoodsIcon size={1} icon="arrowDownShort" />}
                onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                }}
            >
                {t("component.grid.filters.save_view", "Save view")}
            </Button>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                disableRestoreFocus={true}
                disableEnforceFocus={true}
                sx={{
                    mt: 1,
                    borderRadius: "12px",
                }}
            >
                <SaveViewPopoverContent
                    viewFilter={viewFilter}
                    onAction={() => {
                        handleClose();
                    }}
                />
            </Popover>
        </Fragment>
    );
};

type SaveViewPopoverContentProps = {
    viewFilter: ViewFilters;
    onAction: () => void;
};
const SaveViewPopoverContent = (props: SaveViewPopoverContentProps) => {
    const { viewFilter, onAction } = props;

    const { t } = useTranslation();
    const theme = useTheme();

    const isDefault = viewFilter.selectedFilter?.filterName === viewFilter.allFilters[0].filterName;
    const noViewSelected = "";
    const [viewName, setViewName] = useState(noViewSelected);

    useEffect(() => {
        const initialValue =
            isDefault === true ? noViewSelected
            : viewFilter.selectedFilter === undefined ? noViewSelected
            : viewFilter.selectedFilter.filterName;

        setViewName(initialValue);
    }, [viewFilter]);

    const isCurrentViewSelected = viewFilter?.selectedFilter !== undefined;
    return (
        <Stack gap={theme.spacing(1)}>
            <Stack gap={theme.spacing(3)} padding={theme.spacing(3)}>
                <Typography variant="body-bold">{`${t("View name", "View name")}*`}</Typography>
                <FoodsTextInput
                    value={viewName}
                    onChange={(newValue) => {
                        setViewName(newValue);
                    }}
                />
            </Stack>

            <Box sx={{ borderBottom: `1px solid ${theme.custom.grey[500]}` }} />

            {isCurrentViewSelected && isDefault === false ?
                <ExistingViewButtons viewFilter={viewFilter} onAction={onAction} currentViewName={viewName} />
            :   <NoViewButtons viewFilter={viewFilter} onAction={onAction} currentViewName={viewName} />}
        </Stack>
    );
};

type NoViewButtonsProps = {
    currentViewName: string;
    viewFilter: ViewFilters;
    onAction: () => void;
};
const NoViewButtons = (props: NoViewButtonsProps) => {
    const { viewFilter, onAction, currentViewName } = props;

    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Stack padding={theme.spacing(3)}>
            <Button
                variant="primary"
                startIcon={<FoodsIcon size={2} icon={"check"} />}
                onClick={() => {
                    viewFilter.addNewFilterView(currentViewName);
                    onAction();
                }}
            >
                {t("view_filters.save_as", "Save new view")}
            </Button>
        </Stack>
    );
};

type ExistingViewButtonsProps = {
    currentViewName: string;
    viewFilter: ViewFilters;
    onAction: () => void;
};
const ExistingViewButtons = (props: ExistingViewButtonsProps) => {
    const { viewFilter, onAction, currentViewName } = props;

    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Stack padding={theme.spacing(3)} gap={theme.spacing(1.5)}>
            <Button
                variant="primary"
                startIcon={<FoodsIcon size={2} icon={"save"} />}
                onClick={() => {
                    if (viewFilter.selectedFilter === undefined) {
                        // Not possible to update unselected
                        return;
                    }
                    viewFilter.updateFilter({
                        filterNameToUpdate: viewFilter.selectedFilter.filterName,
                        newFilterName: currentViewName,
                    });
                    onAction();
                }}
            >
                {t("view_filters.save_selected", "Save changes")}
            </Button>
            <Button
                variant="primary"
                startIcon={<FoodsIcon size={2} icon={"duplicate"} />}
                onClick={() => {
                    viewFilter.addNewFilterView(currentViewName);
                    onAction();
                }}
            >
                {t("view_filters.save_as", "Save new view")}
            </Button>
            <Button
                variant={"text"}
                startIcon={<FoodsIcon size={2} icon={"delete"} />}
                onClick={() => {
                    viewFilter.deleteSelectedFilter();
                    onAction();
                }}
            >
                {t("view_filters.delete_select", "Delete view")}
            </Button>
        </Stack>
    );
};
